import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Camera, Loader2, X, CheckCircle, AlertCircle } from 'lucide-react';
import ProductExtractionResults from '../../components/survey/ProductExtractionResults';
import PhotoValidationModal from '../../components/survey/PhotoValidationModal';

const API_URL = import.meta.env.VITE_API_URL || '';

interface ExtractedProduct {
  name: string;
  brand?: string | null;
  category?: string | null;
  confidence: number;
  quantity?: number | null;
}

interface UploadedImage {
  s3_key: string;
  url: string;
  extracted_products: ExtractedProduct[];
  extraction_status: 'pending' | 'processing' | 'completed' | 'failed';
  image_quality?: string | null;
  upload_timestamp: string;
  localPreview?: string;
}

interface SessionData {
  survey_id: number;
  session_id: number;
  question_id: string;
  images: UploadedImage[];
}

const MobilePhotoUpload: React.FC = () => {
  const { uploadToken } = useParams<{ uploadToken: string }>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const objectUrlsRef = useRef<string[]>([]);

  const [session, setSession] = useState<SessionData | null>(null);
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpired, setIsExpired] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [validationModal, setValidationModal] = useState<{
    isOpen: boolean;
    error: string;
    s3Key: string;
  }>({ isOpen: false, error: '', s3Key: '' });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Abort any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Revoke all object URLs
      objectUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  // Auto-clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (uploadToken) {
      validateSession();
    }
  }, [uploadToken]);

  const validateSession = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/photo-upload/session/${uploadToken}/status`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error('Session not found');
      }

      const data = await response.json();

      if (data.expired) {
        setIsExpired(true);
        setError('This upload session has expired. Please scan the QR code again.');
      } else {
        setSession({
          survey_id: 0,
          session_id: data.session_id,
          question_id: data.question_id,
          images: data.images || []
        });
        setImages(data.images || []);
      }
    } catch (err) {
      setError('Invalid or expired upload session');
      setIsExpired(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCapture = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files?.length) return;

    if (!session || !uploadToken) {
      setError('Session not ready. Please wait and try again.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setIsUploading(true);
    setError(null);

    // Create new abort controller for this batch
    abortControllerRef.current = new AbortController();

    for (const file of Array.from(files)) {
      if (file.size > 15 * 1024 * 1024) {
        setError('File size exceeds 15MB limit');
        continue;
      }

      const localPreview = URL.createObjectURL(file);
      objectUrlsRef.current.push(localPreview);

      try {
        // Convert file to base64 for token-based upload
        const reader = new FileReader();
        const base64Promise = new Promise<string>((resolve, reject) => {
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const base64 = await base64Promise;

        // Use token-based upload (updates Redis session for desktop sync)
        const response = await fetch(`${API_URL}/api/photo-upload/upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            upload_token: uploadToken,
            image_base64: base64
          }),
          credentials: 'include',
          signal: abortControllerRef.current.signal
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Upload failed');
        }

        const result = await response.json();

        const uploadedImage: UploadedImage = {
          ...result.image,
          localPreview
        };

        // Check if needs validation BEFORE adding to state
        const needsValidation =
          uploadedImage.extraction_status === 'failed' ||
          uploadedImage.image_quality === 'poor' ||
          uploadedImage.image_quality === 'unclear' ||
          (uploadedImage.extracted_products || []).length === 0;

        if (needsValidation) {
          // Add image to state but show validation modal
          setImages(prev => [...prev, uploadedImage]);
          setValidationModal({
            isOpen: true,
            error: 'Image quality is poor or no products detected. Please take a clearer photo.',
            s3Key: uploadedImage.s3_key
          });
        } else {
          // Good image, just add to state
          setImages(prev => [...prev, uploadedImage]);
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          // Request was aborted, clean up and exit
          URL.revokeObjectURL(localPreview);
          objectUrlsRef.current = objectUrlsRef.current.filter(u => u !== localPreview);
          break;
        }
        URL.revokeObjectURL(localPreview);
        objectUrlsRef.current = objectUrlsRef.current.filter(u => u !== localPreview);
        setError(err instanceof Error ? err.message : 'Upload failed');
      }
    }

    setIsUploading(false);
    // Always reset file input to allow re-selecting the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = async (s3Key: string) => {
    const imageToRemove = images.find(img => img.s3_key === s3Key);
    if (imageToRemove?.localPreview) {
      URL.revokeObjectURL(imageToRemove.localPreview);
      objectUrlsRef.current = objectUrlsRef.current.filter(u => u !== imageToRemove.localPreview);
    }
    setImages(prev => prev.filter(img => img.s3_key !== s3Key));

    // Also remove from Redis session so desktop doesn't see the removed image
    if (uploadToken) {
      try {
        await fetch(
          `${API_URL}/api/photo-upload/session/${uploadToken}/image/${encodeURIComponent(s3Key)}`,
          { method: 'DELETE', credentials: 'include' }
        );
      } catch (err) {
        console.error('Failed to remove image from session:', err);
      }
    }
  };

  const handleRetake = async () => {
    const s3Key = validationModal.s3Key;
    setValidationModal({ isOpen: false, error: '', s3Key: '' });
    await handleRemoveImage(s3Key);
    // User will tap "Take Photo" button to retake - more reliable on mobile
  };

  const handleContinueAnyway = () => {
    setValidationModal({ isOpen: false, error: '', s3Key: '' });
  };

  const handleDone = () => {
    setIsDone(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FEFEFE] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#07E5D1]" />
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="min-h-screen bg-[#FEFEFE] flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-800 mb-2">Session Expired</h1>
          <p className="text-gray-600">
            {error || 'This upload session has expired. Please scan the QR code again from your computer.'}
          </p>
        </div>
      </div>
    );
  }

  if (isDone) {
    return (
      <div className="min-h-screen bg-[#FEFEFE] flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <CheckCircle className="w-16 h-16 text-[#07E5D1] mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-gray-800 mb-2">Photos Uploaded!</h1>
          <p className="text-gray-600 mb-4">
            Your {images.length} photo{images.length !== 1 ? 's' : ''} {images.length !== 1 ? 'have' : 'has'} been uploaded successfully.
          </p>
          <p className="text-sm text-gray-500">
            You can close this page and return to your computer to continue the survey.
          </p>
        </div>
      </div>
    );
  }

  // Only disable Done during active upload - extraction happens in background
  const isProcessing = isUploading;

  return (
    <div className="min-h-screen bg-[#FEFEFE]">
      <header className="bg-white border-b border-gray-100 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold text-gray-800">Photo Upload</h1>
          {images.length > 0 && (
            <button
              onClick={handleDone}
              disabled={isProcessing}
              className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors
                         ${isProcessing
                           ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                           : 'bg-[#07E5D1] text-white hover:bg-[#06D4C2]'}`}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Done
                </>
              )}
            </button>
          )}
        </div>
      </header>

      <main className="p-4 space-y-4">
        <input
          id="mobile-camera-input"
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleCapture}
          className="hidden"
        />

        {images.length === 0 ? (
          <label
            htmlFor={isUploading ? undefined : "mobile-camera-input"}
            className={`w-full py-12 border-2 border-dashed border-[#07E5D1] rounded-xl
                       bg-[#E8FCFA] hover:bg-[#d0f9f5] transition-colors flex flex-col
                       items-center justify-center gap-4 cursor-pointer
                       ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isUploading ? (
              <>
                <Loader2 className="w-12 h-12 text-[#07E5D1] animate-spin" />
                <span className="text-lg font-medium text-[#06D4C2]">Uploading & Analyzing...</span>
                <span className="text-sm text-[#07E5D1]">Please wait while we process your photo</span>
              </>
            ) : (
              <>
                <Camera className="w-12 h-12 text-[#07E5D1]" />
                <span className="text-xl font-medium text-[#06D4C2]">Take Photo</span>
                <span className="text-sm text-[#07E5D1]">Tap to capture your pantry or fridge</span>
              </>
            )}
          </label>
        ) : (
          <div className="space-y-4">
            {images.map((image) => (
              <div key={image.s3_key} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <div className="aspect-video relative bg-gray-100">
                  <img
                    src={image.localPreview || `${API_URL}${image.url}`}
                    alt="Uploaded photo"
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={() => handleRemoveImage(image.s3_key)}
                    className="absolute top-3 right-3 p-2 bg-red-500 text-white rounded-full
                               hover:bg-red-600 transition-colors shadow-md"
                  >
                    <X className="w-5 h-5" />
                  </button>

                  {image.extraction_status === 'processing' && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <div className="bg-white rounded-lg px-4 py-3 flex items-center gap-3">
                        <Loader2 className="w-5 h-5 animate-spin text-[#07E5D1]" />
                        <span className="font-medium">Analyzing products...</span>
                      </div>
                    </div>
                  )}
                </div>

                {image.extraction_status === 'completed' && image.extracted_products?.length > 0 && (
                  <ProductExtractionResults
                    products={image.extracted_products}
                    showImage={false}
                  />
                )}

                {image.extraction_status === 'failed' && (
                  <div className="p-3 bg-amber-50 text-amber-700 text-sm">
                    No products detected in this image
                  </div>
                )}
              </div>
            ))}

            <label
              htmlFor={isProcessing ? undefined : "mobile-camera-input"}
              className={`w-full py-4 border-2 border-dashed rounded-xl transition-colors
                         flex items-center justify-center gap-2 cursor-pointer
                         ${isProcessing
                           ? 'border-[#07E5D1] bg-[#E8FCFA] cursor-wait'
                           : 'border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-[#07E5D1]'}`}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin text-[#07E5D1]" />
                  <span className="text-[#06D4C2] font-medium">
                    {isUploading ? 'Uploading & Analyzing...' : 'Analyzing image...'}
                  </span>
                </>
              ) : (
                <>
                  <Camera className="w-5 h-5 text-gray-500" />
                  <span className="text-gray-600 font-medium">Add Another Photo</span>
                </>
              )}
            </label>
          </div>
        )}

        {error && (
          <p className="text-sm text-red-600 text-center bg-red-50 p-3 rounded-lg">{error}</p>
        )}

        <p className="text-xs text-gray-500 text-center">
          Photos are securely uploaded and analyzed for products
        </p>
      </main>

      <PhotoValidationModal
        isOpen={validationModal.isOpen}
        error={validationModal.error}
        onRetake={handleRetake}
        onContinueAnyway={handleContinueAnyway}
      />
    </div>
  );
};

export default MobilePhotoUpload;
